// src/components/LandingPage.js
import React from "react";
import { Button, Card, Carousel, Navbar } from "flowbite-react";
import { HiOutlineArrowRight, HiOutlineChartPie, HiOutlineClipboardList, HiOutlineCube } from "react-icons/hi";

const LandingPage = ({ onNavigate }) => {
  return (
    <div className="landing-page bg-white dark:bg-gray-900">
      {/* Navbar */}
      <Navbar fluid rounded>
        <Navbar.Brand href="/">
          <h1 className="text-4xl font-bold text-cyan-400">TradeWise</h1>
        </Navbar.Brand>
        <div className="flex md:order-2">
          <Button onClick={() => onNavigate("login")} className="mr-2">
            Log in
          </Button>
          <Button color="success" onClick={() => onNavigate("login")}>
            Sign up
          </Button>
          <Navbar.Toggle />
        </div>
      </Navbar>

      {/* Hero Section */}
      <section className="bg-white dark:bg-gray-900">
        <div className="py-8 px-4 mx-auto max-w-screen-xl text-center lg:py-16 lg:px-12">
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight leading-none text-gray-900 md:text-5xl lg:text-6xl dark:text-white">
            TradeWise: Elevate Your Trading Journey
          </h1>
          <p className="mb-8 text-lg font-normal text-gray-500 lg:text-xl sm:px-16 xl:px-48 dark:text-gray-400">
            Turn your trades into insights. Journal smarter, trade better.
          </p>
          <div className="flex flex-col mb-8 lg:mb-16 space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
            <Button size="xl" onClick={() => onNavigate("login")}>
              Get started
              <HiOutlineArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[8px] rounded-t-xl h-[172px] max-w-[301px] md:h-[414px] md:max-w-[720px]">
            <div className="rounded-lg overflow-hidden h-[156px] md:h-[398px] bg-white dark:bg-gray-800">
              <img
                src="/images/TWApp.png"
                className="dark:hidden h-[156px] md:h-[398px] w-full rounded-lg"
                alt=""
              />
              <img
                src="/images/TWApp.png"
                className="hidden dark:block h-[156px] md:h-[398px] w-full rounded-lg"
                alt=""
              />
            </div>
          </div>
          <div className="relative mx-auto bg-gray-900 dark:bg-gray-700 rounded-b-xl rounded-t-sm h-[17px] max-w-[351px] md:h-[21px] md:max-w-[780px]">
            <div className="absolute left-1/2 top-0 -translate-x-1/2 rounded-b-xl w-[56px] h-[5px] md:w-[96px] md:h-[8px] bg-gray-800"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 dark:bg-gray-800">
        <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
          <div className="max-w-screen-md mb-8 lg:mb-16">
            <h2 className="mb-4 text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white">
              Designed for traders like you
            </h2>
            <p className="text-gray-500 sm:text-xl dark:text-gray-400">
              Here at TradeWise, we focus on providing you with the tools you need to improve your trading performance.
            </p>
          </div>
          <div className="space-y-8 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-12 md:space-y-0">
            <div>
              <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-primary-100 lg:h-12 lg:w-12 dark:bg-primary-900">
                <HiOutlineChartPie className="w-5 h-5 text-primary-600 lg:w-6 lg:h-6 dark:text-primary-300" />
              </div>
              <h3 className="mb-2 text-xl font-bold dark:text-white">Comprehensive Reports</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Gain deep insights into your trading performance with our detailed analytics.
              </p>
            </div>
            <div>
              <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-primary-100 lg:h-12 lg:w-12 dark:bg-primary-900">
                <HiOutlineClipboardList className="w-5 h-5 text-primary-600 lg:w-6 lg:h-6 dark:text-primary-300" />
              </div>
              <h3 className="mb-2 text-xl font-bold dark:text-white">Intuitive Trading Journal</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Record and reflect on every trade with ease using our user-friendly interface.
              </p>
            </div>
            <div>
              <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-primary-100 lg:h-12 lg:w-12 dark:bg-primary-900">
                <HiOutlineCube className="w-5 h-5 text-primary-600 lg:w-6 lg:h-6 dark:text-primary-300" />
              </div>
              <h3 className="mb-2 text-xl font-bold dark:text-white">Advanced Trading Analysis</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Identify patterns and optimize your strategies with our powerful analysis tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white dark:bg-gray-900">
        <div className="py-8 px-4 mx-auto max-w-screen-xl text-center lg:py-16 lg:px-6">
          <div className="mx-auto max-w-screen-sm">
            <h2 className="mb-4 text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white">Testimonials</h2>
            <p className="mb-8 font-light text-gray-500 lg:mb-16 sm:text-xl dark:text-gray-400">
              Explore what our users have to say about TradeWise
            </p>
          </div>
          <Carousel>
            <div className="flex flex-col items-center pb-10">
              <img className="mb-3 w-24 h-24 rounded-full shadow-lg" src="/path-to-image.jpg" alt="Trader" />
              <h5 className="mb-1 text-xl font-medium text-gray-900 dark:text-white">John Doe</h5>
              <span className="text-sm text-gray-500 dark:text-gray-400">Day Trader</span>
              <p className="mt-4 mb-4 text-base text-gray-500 dark:text-gray-400 max-w-2xl">
                "TradeWise has completely transformed my trading. The insights I've gained have been invaluable."
              </p>
            </div>
            {/* Add more testimonials as needed */}
          </Carousel>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 dark:bg-gray-800">
        <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
          <div className="mx-auto max-w-screen-sm text-center">
            <h2 className="mb-4 text-4xl tracking-tight font-extrabold leading-tight text-gray-900 dark:text-white">
              Start your trading journey today
            </h2>
            <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
            Try TradeWise for free for a limited time, before server costs increase!
            </p>
            <Button size="xl" onClick={() => onNavigate("login")}>
              Get started for free
              <HiOutlineArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
